import { useEffect, useRef } from 'react'
import { Alert, Spinner } from '@/components/ds2'
import { useGeneralAgentChatStore } from '../hooks/generalAgentChatStore'
import { GeneralAgentComposer } from './GeneralAgentComposer'
import { GeneralAgentMessageList } from './GeneralAgentMessageList'
import { ToolQuestionThread } from './approval/ToolQuestionThread'
import { useCardResultsStore } from './results/cardResultsStore'

/** How close to the bottom counts as reading the newest message (dispatch-help's thread uses the same). */
const PINNED_PX = 40

/** The general agent's conversation and composer. The page and the floating panel both render it over one store. */
export const GeneralAgentChat = () => {
    const messages = useGeneralAgentChatStore((s) => s.messages)
    const state = useGeneralAgentChatStore((s) => s.state)
    const send = useGeneralAgentChatStore((s) => s.send)
    const restore = useGeneralAgentChatStore((s) => s.restore)
    const tabId = useGeneralAgentChatStore((s) => s.tabId)
    const pendingQuestions = useGeneralAgentChatStore((s) => s.pendingQuestions)
    const heldNotices = useGeneralAgentChatStore((s) => s.heldNotices)
    // The card results are read after the turn's messages and drawn between them, so they move the bottom too.
    const cards = useCardResultsStore((s) => (s.tabId === tabId ? s.cards : null))

    const scroller = useRef<HTMLDivElement>(null)
    // Whether the user is at the bottom, so the thread follows what comes in; scrolled up to read a table, they stay where they are.
    const pinned = useRef(true)
    const lastTop = useRef(0)

    // After a reload, bring back the conversation this browser tab had open. Run again once the launcher has bound the
    // signed-in user, which hands over the saved chat: on the full page this effect runs before that.
    useEffect(() => {
        void restore()
    }, [restore, tabId])

    // Another chat opens at its newest message.
    useEffect(() => {
        pinned.current = true
        lastTop.current = 0
    }, [tabId])

    // Keep the newest message in view while the user is at the bottom: a sent message, 'Thinking…', the reply, the card results
    // drawn above it and a card's question all land there. Their own send brings them back down, however far up they were.
    useEffect(() => {
        if (messages.at(-1)?.id.startsWith('local-')) pinned.current = true
        const el = scroller.current
        if (el && pinned.current) el.scrollTop = el.scrollHeight
    }, [messages, state.status, pendingQuestions, cards, heldNotices.length])

    // Scrolling up leaves the bottom; coming back within `PINNED_PX` of it follows again (scrollbar and keyboard included). The distance
    // is read first: content that shrinks at the bottom (an answered card pruned for a shorter reply) clamps `scrollTop` and fires a
    // scroll that looks like scrolling up, but the view is still at the bottom.
    const onScroll = () => {
        const el = scroller.current
        if (!el) return
        if (el.scrollHeight - el.scrollTop - el.clientHeight < PINNED_PX) pinned.current = true
        else if (el.scrollTop < lastTop.current - 2) pinned.current = false
        lastTop.current = el.scrollTop
    }

    return (
        <div className="flex h-full min-h-0 flex-col gap-3">
            <div ref={scroller} onScroll={onScroll} className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                <GeneralAgentMessageList messages={messages} waiting={state.status === 'waiting'} />
                <ToolQuestionThread />
                {/* Under the card questions too: an answered question resumes the turn, so the agent's work comes after it. */}
                {state.status === 'waiting' && (
                    <p role="status" className="mt-3 flex items-center gap-2 self-start text-sm text-foreground-muted">
                        <Spinner size={14} /> Thinking…
                    </p>
                )}
                {heldNotices.length > 0 && (
                    <p role="status" className="mt-2 self-start text-xs text-foreground-muted">
                        Tells the agent once nothing waits on you: {heldNotices.join(' · ')}
                    </p>
                )}
            </div>
            {state.status === 'error' && <Alert variant="danger" title={state.message} />}
            <GeneralAgentComposer onSend={send} />
        </div>
    )
}
